import { useAtom, useSetAtom } from 'jotai';
import { atomWithReset, RESET } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert, AlertTitle, Stack, Typography, Button, Slide } from '@mui/material';

import { selectedDeviceAtom } from '@/components/common/MapView';

import type { SyntheticEvent } from 'react';
import type { SlideProps, SnackbarCloseReason } from '@mui/material';

export const snackbarDataAtom = atomWithReset({ open: false, isFDA: false, deviceName: '', error: false });

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

interface SuccessAlertProps {
  deviceName: string;
  isFDA: boolean;
  onGoToClick: () => void;
}
function SuccessAlert({ deviceName, isFDA, onGoToClick }: SuccessAlertProps) {
  const { t } = useTranslation();
  return (
    <>
      <AlertTitle>{t('Devices.label.success')}</AlertTitle>
      <Stack direction="column" gap={1}>
        <Typography>{t('Devices.message.generateSuccess')}</Typography>
        <Typography>
          {t('Devices.label.name')}: {deviceName}
        </Typography>
        <Button variant="contained" onClick={onGoToClick} sx={{ alignSelf: 'flex-end' }}>
          {isFDA ? t('FDA.label.goToElectronicSignature') : t('Devices.label.goToReports')}
        </Button>
      </Stack>
    </>
  );
}

interface ErrorAlertProps {
  deviceName: string;
}
function ErrorAlert({ deviceName }: ErrorAlertProps) {
  const { t } = useTranslation();
  return (
    <>
      <AlertTitle>{t('Devices.label.error')}</AlertTitle>
      <Stack direction="column" gap={1}>
        <Typography>{t('Devices.message.generateFail')}</Typography>
        <Typography>
          {t('Devices.label.name')}: {deviceName}
        </Typography>
      </Stack>
    </>
  );
}

export default function ReportSnackbar() {
  const [{ open, isFDA, deviceName, error }, setData] = useAtom(snackbarDataAtom);
  const setSelectedDevice = useSetAtom(selectedDeviceAtom);

  const navigate = useNavigate();

  function onSnackbarClose(_: SyntheticEvent | Event, reason?: SnackbarCloseReason) {
    if (reason === 'clickaway') return;
    setData(RESET);
  }
  function onGoToClick() {
    setSelectedDevice(null);
    if (isFDA) {
      navigate('/fda/signature', { replace: false });
    } else {
      navigate('/reports', { replace: false });
    }
  }

  return (
    <Snackbar
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      TransitionComponent={SlideTransition}
      autoHideDuration={5000}
      open={open}
      onClose={onSnackbarClose}
    >
      <Alert severity={error ? 'error' : 'success'} action={false} sx={{ boxShadow: 4 }}>
        {error ? (
          <ErrorAlert deviceName={deviceName} />
        ) : (
          <SuccessAlert deviceName={deviceName} isFDA={isFDA} onGoToClick={onGoToClick} />
        )}
      </Alert>
    </Snackbar>
  );
}
